// Plain-language "what was this?" quick choices for the bank-line review
// drawer. Each choice resolves to an existing chart account by code/name and
// only pre-selects it — the user still confirms the categorize request, and
// nothing posts from here. An intent with no matching account is hidden
// rather than inventing one.
import type { ChartAccount } from "@/lib/api";

type QuickChoiceDirection = "money_in" | "money_out";

interface QuickChoice {
  key: string;
  label: string;
  account: ChartAccount;
  hint: string;
}

interface QuickChoiceIntent {
  key: string;
  label: string;
  directions: readonly QuickChoiceDirection[];
  match: (accounts: ChartAccount[]) => ChartAccount | undefined;
  hint: (account: ChartAccount) => string;
}

const TAX_WORDS = /tax|vat|gst/i;
const DRAWING_WORDS = /drawing|owner|director|capital/i;
const CASH_WORDS = /cash|bank/i;

function isActive(account: ChartAccount): boolean {
  return account.active !== false;
}

function firstMatch(
  accounts: ChartAccount[],
  predicate: (account: ChartAccount) => boolean,
): ChartAccount | undefined {
  return accounts.filter(isActive).find(predicate);
}

const INTENTS: QuickChoiceIntent[] = [
  {
    key: "business_cost",
    label: "A business cost",
    directions: ["money_out"],
    match: (accounts) =>
      firstMatch(accounts, (account) => account.account_type === "expense"),
    hint: (account) => `Posts this line as a business expense to ${account.code} — ${account.name}.`,
  },
  {
    key: "money_received",
    label: "Money I received",
    directions: ["money_in"],
    match: (accounts) =>
      firstMatch(accounts, (account) => account.account_type === "revenue"),
    hint: (account) => `Posts this line as income to ${account.code} — ${account.name}.`,
  },
  {
    key: "moved_between",
    label: "Moved between my accounts",
    directions: ["money_in", "money_out"],
    match: (accounts) =>
      firstMatch(
        accounts,
        (account) =>
          account.account_type === "asset" &&
          (account.sub_type === "cash" ||
            account.sub_type === "bank" ||
            CASH_WORDS.test(account.name) ||
            account.code === "1100"),
      ),
    hint: (account) => `Records a movement to ${account.code} — ${account.name}.`,
  },
  {
    key: "paid_tax",
    label: "Paid the tax office",
    directions: ["money_out"],
    match: (accounts) =>
      firstMatch(
        accounts,
        (account) =>
          account.account_type === "liability" && TAX_WORDS.test(account.name),
      ),
    hint: (account) => `Clears tax owed against ${account.code} — ${account.name}.`,
  },
  {
    key: "money_took_out",
    label: "Money I took out",
    directions: ["money_out"],
    match: (accounts) =>
      firstMatch(
        accounts,
        (account) =>
          account.account_type === "equity" && DRAWING_WORDS.test(account.name),
      ),
    hint: (account) => `Records this as drawings against ${account.code} — ${account.name}.`,
  },
  {
    key: "personal",
    label: "Personal — not business",
    directions: ["money_in", "money_out"],
    match: (accounts) =>
      firstMatch(
        accounts,
        (account) =>
          account.account_type === "equity" && DRAWING_WORDS.test(account.name),
      ),
    hint: (account) =>
      `Records this as not-business against ${account.code} — ${account.name}.`,
  },
];

/** Resolves the quick choices that have a matching chart account for the
 * money direction of one bank line. Unmatched intents are omitted. */
export function resolveQuickChoices(
  accounts: ChartAccount[],
  direction: QuickChoiceDirection,
): QuickChoice[] {
  const resolved: QuickChoice[] = [];
  for (const intent of INTENTS) {
    if (!intent.directions.includes(direction)) continue;
    const account = intent.match(accounts);
    if (!account) continue;
    resolved.push({
      key: intent.key,
      label: intent.label,
      account,
      hint: intent.hint(account),
    });
  }
  return resolved;
}
